'use client';

import * as React from 'react';
import { useFirestore, useAuth } from '@/firebase';
import { getOrCreateAlbum } from '@/lib/gallery';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';
import { AlbumType } from '@/models/album';

interface CreateAlbumDialogProps {
    open: boolean;
    onClose: () => void;
    parentId: string | null;
    parentPath: string | null;
    onComplete: () => void;
}

export function CreateAlbumDialog({
    open,
    onClose,
    parentId,
    parentPath,
    onComplete,
}: CreateAlbumDialogProps) {
    const firestore = useFirestore();
    const auth = useAuth();
    const { toast } = useToast();

    const [name, setName] = React.useState('');
    const [description, setDescription] = React.useState('');
    const [saving, setSaving] = React.useState(false);

    // Reset form when dialog opens
    React.useEffect(() => {
        if (open) {
            setName('');
            setDescription('');
        }
    }, [open]);

    const handleSave = async () => {
        if (!firestore || !auth.currentUser || !name.trim()) return;

        setSaving(true);
        try {
            // Calculate full path
            // Logic: currentPath + / + name (slugified inside getOrCreateAlbum via regex or simple concat)
            // Actually `getOrCreateAlbum` helper handles creation logic, but usually takes a full path.
            // But we might want to ensure we're creating a *sub-album*.

            // Let's manually construct the path to ensure it's correct before calling helper?
            // Helper `getOrCreateAlbum` takes `albumPath`.
            // If parentPath is "/foo", and new name is "Bar", path should be "/foo/bar" (normalized).

            // We will rely on `getOrCreateAlbum` to slugify the last segment if needed, 
            // but `getOrCreateAlbum` typically expects the path to be the primary key.

            const slug = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
            const cleanParentPath = parentPath ? (parentPath.startsWith('/') ? parentPath.substring(1) : parentPath) : '';
            const newPath = cleanParentPath ? `${cleanParentPath}/${slug}` : slug;

            await getOrCreateAlbum(firestore, newPath, auth.currentUser.uid, {
                name: name.trim(),
                description: description.trim(),
                parentAlbumId: parentId || undefined, // undefined for root in helper? check interface
                albumType: AlbumType.CUSTOM,
                autoDateDetection: false
            });

            // Force cache sync
            const token = await auth.currentUser.getIdToken();
            fetch('/api/gallery/sync-hierarchy', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ role: 'members' }),
            }).catch(console.warn);

            toast({
                title: 'Album Created',
                description: `"${name}" has been created.`,
            });

            onComplete?.();
            onClose();
        } catch (error: any) {
            console.error('Error creating album:', error);
            toast({
                variant: 'destructive',
                title: 'Creation Failed',
                description: error.message || 'Could not create album.',
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Create New Album</DialogTitle>
                    <DialogDescription>
                        Create a new folder inside <strong>{parentPath ? parentPath.split('/').pop() : 'Root'}</strong>.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    <div className="space-y-2">
                        <Label htmlFor="album-name">Name</Label>
                        <Input
                            id="album-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. Vacation 2025"
                            autoFocus
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="album-description">Description (Optional)</Label>
                        <Textarea
                            id="album-description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="What's in this album?"
                            className="resize-none"
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={saving || !name.trim()}>
                        {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Create Album
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
